import { useEffect, useState } from 'react';
import { api } from '../api';
import {
  ASSESSMENT_SECTIONS,
  DOCUMENT_LABELS,
  QUALIFICATION_LABELS,
  SUPPLIER_ASSESSMENT_QUESTIONS,
  type SupplierAssessmentState,
  type SupplierQualification,
} from '../shared/supplier-assessment';

const labels: Record<keyof SupplierQualification, string> = {
  responsible_person: '公司負責人',
  contact_line: 'LINE',
  supplier_qualification_types: '供應商資格',
  qualification_other: '其他資格說明',
  drug_dealer_license_number: '藥商許可執照字號',
  drug_manufacturer_license_number: '藥廠許可／製造字號',
  gdp_status: 'GDP狀態',
  gdp_issue_date: 'GDP發證日期',
  gdp_expiry_date: 'GDP有效日期',
  gdp_approved_scopes: 'GDP核准範圍',
  has_pharmaceutical_transport: '涉及藥品配送／運輸',
  transport_responsibility: '運輸責任者',
  transport_company_name: '配送運輸業者',
  transport_temperature_conditions: '運送溫度',
  transport_temperature_other: '其他運送溫度',
  transport_methods: '運輸方式',
  transport_notes: '運輸備註',
  warehouse_type: '倉儲方式',
  warehouse_company_name: '委外倉儲公司',
  filled_by_name: '填表人',
  filled_by_title: '填表人職稱',
  filled_by_email: '填表人Email',
  declaration_accepted: '已確認資料真實完整',
};
const values: Record<string, string> = {
  ...QUALIFICATION_LABELS,
  COMPLETED: '已完成',
  NOT_COMPLETED: '未完成',
  NOT_APPLICABLE: '不適用',
  STORAGE: '儲存',
  TRANSPORT: '運輸',
  SUPPLIER: '供應商',
  THIRD_PARTY: '第三方物流',
  COLD_2_8: '2～8℃',
  CONTROLLED_15_25: '15～25℃',
  BELOW_25: '25℃以下',
  BELOW_30: '30℃以下',
  AMBIENT: '常溫',
  A: '空調車',
  B: '非空調車',
  C: '確效包裝',
  D: '連續溫度記錄',
  E: '溫度偏離警報',
  F: '其他',
  OWN: '自有倉儲',
  OUTSOURCED: '委外倉儲',
  BOTH: '兩者皆有',
};

export function SupplierAssessmentReview({ applicationId }: { applicationId: string }) {
  const [state, setState] = useState<SupplierAssessmentState | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setState(null);
    setError('');
    api.vendorApplications
      .assessment(applicationId)
      .then((value) => {
        if (active) setState(value);
      })
      .catch((error) => {
        if (active) setError(error instanceof Error ? error.message : '供應商評估載入失敗');
      });
    return () => {
      active = false;
    };
  }, [applicationId]);
  if (error)
    return (
      <p role="alert" className="rounded-xl bg-rose-50 p-4 text-rose-800">
        {error}
      </p>
    );
  if (!state) return <p role="status">載入供應商評估…</p>;
  if (!state.revision)
    return (
      <p className="rounded-xl bg-slate-50 p-4">
        此申請尚未建立新版供應商評估問卷；既有申請內容仍保留。
      </p>
    );
  const enumFields = new Set(['supplier_qualification_types','gdp_status','gdp_approved_scopes','transport_responsibility','transport_temperature_conditions','transport_methods','warehouse_type']);
  const display = (value: unknown, field: string): string =>
    Array.isArray(value)
      ? value.map(item => display(item,field)).join('、')
      : typeof value === 'boolean'
        ? value
          ? '是'
          : '否'
        : (enumFields.has(field) ? values[String(value)] ?? String(value || '—') : String(value || '—'));
  return (
    <section className="space-y-4 rounded-xl border border-slate-200 p-5">
      <h3 className="text-lg font-bold">供應商資格與評估摘要</h3>
      <p className="text-sm text-slate-500">
        問卷版本：{state.draft.template_version}；回答否列為注意事項，不自動拒絕、不計分。
      </p>
      <dl className="grid gap-4 sm:grid-cols-2">
        {Object.entries(state.draft.qualification).map(([key, value]) => (
          <div key={key}>
            <dt className="text-xs text-slate-500">{labels[key as keyof SupplierQualification]}</dt>
            <dd className="mt-1 whitespace-pre-wrap break-words">{display(value,key)}</dd>
          </div>
        ))}
      </dl>
      <div className="grid gap-3 sm:grid-cols-2">
        {Object.entries(state.evaluation.sections).map(([section, summary]) => (
          <p key={section} className="rounded-xl bg-slate-50 p-3">
            {ASSESSMENT_SECTIONS[section as keyof typeof ASSESSMENT_SECTIONS]}：
            {summary.applicability === 'NOT_APPLICABLE'
              ? '不適用'
              : `是 ${summary.yes}／否 ${summary.no}／不適用 ${summary.notApplicable}`}
          </p>
        ))}
      </div>
      <div className="rounded-xl bg-amber-50 p-4">
        <h4 className="font-bold">需注意事項（{state.evaluation.warnings.length} 項）</h4>
        {state.evaluation.warnings.map((w) => (
          <p key={w.path} className="mt-2 text-sm">
            {w.message}
          </p>
        ))}
      </div>
      <details>
        <summary className="cursor-pointer font-bold">查看完整問卷</summary>
        {SUPPLIER_ASSESSMENT_QUESTIONS.map((q) => {
          const answer = state.draft.answers[q.question_code];
          return (
            <div key={q.question_code} className="mt-3 rounded-xl bg-slate-50 p-3">
              <p>
                {q.question_code} {q.question_text}
              </p>
              <p className="mt-2 font-semibold">
                {answer?.answer
                  ? { YES: '是', NO: '否', NOT_APPLICABLE: '不適用' }[answer.answer]
                  : '未填寫／不適用'}
              </p>
              {answer?.certification_name && <p>{answer.certification_name}</p>}
              {answer?.comment && <p className="whitespace-pre-wrap">{answer.comment}</p>}
            </div>
          );
        })}
      </details>
      <h4 className="font-bold">資格與問卷文件</h4>
      {state.documents.map((doc) => (
        <a
          key={doc.id}
          className="block text-sm text-teal-800 underline"
          target="_blank"
          rel="noreferrer"
          href={`/api/vendor-applications/${encodeURIComponent(applicationId)}/assessment/documents/${encodeURIComponent(doc.id)}`}
        >
          {DOCUMENT_LABELS[doc.document_type]}｜{doc.file_name}｜
          {
            { VALID: '有效', EXPIRING_SOON: '即將到期', EXPIRED: '已過期', UNKNOWN: '未設定效期' }[
              doc.status
            ]
          }
        </a>
      ))}
    </section>
  );
}
